import { useEffect, useState } from 'react';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import GalleryService from '@/services/gallery.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import FormError from '@/shared-components/error/components/FormError';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import SettingsLoadingPage from '@/shared-components/loaders/components/SettingsLoadingPage';
import SettingsHeading from '@/shared-components/text/components/SettingsHeading';
import { resolveSupportedCurrencyCode } from '@/utils/currency.utils';

import './index.css';

export default function PriceSettings() {
  const { state, stateHandler } = useStateHandler();
  const t = useTranslations('common.user_settings.price');
  const [currency, setCurrency] = useState(null);
  const [initialCurrency, setInitialCurrency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { response, data } = await GalleryService.getGalleryPricing(state?.user_session?._id ?? null);
        if (!active) return;
        const nextCurrency = response.ok
          ? resolveSupportedCurrencyCode(data?.default_artwork_currency)
          : null;
        if (!nextCurrency) throw new Error('Unable to load the default artwork currency.');
        setCurrency(nextCurrency);
        setInitialCurrency(nextCurrency);
      } catch (requestError) {
        if (active) {
          setError({ error_message: requestError?.message ?? 'Unable to load the default artwork currency.' });
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [state?.user_session?._id]);

  const save = async (event) => {
    event.preventDefault();
    if (!currency || currency === initialCurrency || saving) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const { response, data } = await GalleryService.updateGalleryPricing(currency);
      if (!response.ok) {
        throw new Error(data?.error_message ?? 'Unable to update the default artwork currency.');
      }
      const savedCurrency = resolveSupportedCurrencyCode(data?.default_artwork_currency);
      if (!savedCurrency) throw new Error('Unable to update the default artwork currency.');
      setCurrency(savedCurrency);
      setInitialCurrency(savedCurrency);
      stateHandler.set('user_session.gallery_pricing.default_artwork_currency', savedCurrency);
      setSuccess(true);
    } catch (requestError) {
      setError({ error_message: requestError?.message ?? 'Unable to update the default artwork currency.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SettingsLoadingPage page={t('heading')} />;

  return (
    <>
      <SettingsHeading text={t('heading')} />
      <form className="settings-price__container" onSubmit={save}>
        <PublicFormInput
          placeholder={t('currency_label')}
          type="select"
          element="default_artwork_currency"
          selectOptions={{ options: config.forms.currency_options }}
          value={currency ?? ''}
          onChange={(value) => {
            setCurrency(resolveSupportedCurrencyCode(value));
            setSuccess(false);
          }}
        />
        <FormError error={error} element="default_artwork_currency" />
        <ContinueButton
          type="submit"
          loading={saving}
          success={success}
          successText={t('saved')}
          turnOff={!currency || currency === initialCurrency}
        >
          {t('save')}
        </ContinueButton>
      </form>
    </>
  );
}
